import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Clock3, LogOut, MapPin, Menu, Plus, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePartnerData, type PartnerProfile } from "@/hooks/usePartnerData";
import { partnerNavSections } from "@/data/partnerPortalContent";
import { cn } from "@/lib/utils";
import { detectUserLocation, peekDetectedLocation } from "@/lib/detectUserLocation";

type PartnerLayoutProps = {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  badgeCounts?: {
    applications?: number;
    messages?: number;
  };
};

function resolveLocationLabel(
  profile?: PartnerProfile | null,
  live?: { country: string; city?: string } | null,
) {
  if (live?.city && live?.country) return `${live.city}, ${live.country}`;
  if (live?.country) return live.country;

  const locations = profile?.locations;
  if (Array.isArray(locations)) {
    const first = locations.map((l) => String(l || "").trim()).find(Boolean);
    if (first) return first;
  } else if (typeof locations === "string" && locations.trim()) {
    return locations.trim();
  }
  if (profile?.country?.trim()) return profile.country.trim();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const city = tz.split("/").pop()?.replace(/_/g, " ");
  return city || "Local time";
}

function HeaderTimeLocation({ profile }: { profile?: PartnerProfile | null }) {
  const [now, setNow] = useState(() => new Date());
  const [live, setLive] = useState(() => peekDetectedLocation());
  const locationLabel = useMemo(
    () => resolveLocationLabel(profile, live),
    [profile, live],
  );

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const loc = await detectUserLocation();
      if (!cancelled && loc) setLive(loc);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const time = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const date = now.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="hidden sm:flex flex-col items-end gap-0.5 shrink-0 text-right">
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums text-[#1f2328]">
        <Clock3 className="h-3.5 w-3.5 text-[#656d76]" />
        {time}
      </div>
      <div className="inline-flex items-center gap-1 text-xs text-[#656d76]">
        <MapPin className="h-3 w-3 shrink-0" />
        <span className="truncate max-w-[180px]">
          {date} · {locationLabel}
        </span>
      </div>
    </div>
  );
}

export default function PartnerLayout({
  title,
  subtitle,
  action,
  children,
  badgeCounts,
}: PartnerLayoutProps) {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const { profile } = usePartnerData();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6f8fa]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#d0d7de] border-t-[#00573C]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/partners/login" state={{ from: location.pathname }} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const orgName = profile?.institution_name || user?.name || "Your organization";
  const orgEmail = profile?.email || user?.email || "";
  const canPublish = Boolean(profile?.is_verified);

  const getBadge = (badgeKey?: "applications" | "messages") => {
    if (!badgeKey || !badgeCounts) return 0;
    return badgeCounts[badgeKey] || 0;
  };

  const NavContent = ({ mobile = false }: { mobile?: boolean }) => (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-3 border-b border-[#d0d7de] px-4 py-4">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-[#00573C] text-xs font-bold text-white">
          SA
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[#1f2328]">Partner workspace</p>
          <p className="truncate text-xs text-[#656d76]">Recruit · Review · Publish</p>
        </div>
        {mobile ? (
          <button
            type="button"
            className="rounded-md p-1.5 text-[#656d76] hover:bg-[#f6f8fa]"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div className="border-b border-[#d0d7de] p-3">
        {canPublish ? (
          <Link
            to="/partners/programs/new"
            onClick={() => mobile && setMobileOpen(false)}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[#00573C] px-3 py-2 text-sm font-semibold text-white hover:bg-[#004530]"
          >
            <Plus className="h-4 w-4" />
            Publish program
          </Link>
        ) : (
          <Link
            to="/partners/organization"
            onClick={() => mobile && setMobileOpen(false)}
            title="Complete your profile and get verified to publish"
            className="flex w-full items-center justify-center gap-2 rounded-md border border-[#d0d7de] bg-[#f6f8fa] px-3 py-2 text-sm font-semibold text-[#656d76] hover:bg-[#eef1f4]"
          >
            <Plus className="h-4 w-4" />
            Complete profile to publish
          </Link>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {partnerNavSections.map((section) => (
          <div key={section.title} className="mb-4">
            <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#656d76]">
              {section.title}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const active =
                  location.pathname === item.href ||
                  (item.href !== "/partners/dashboard" &&
                    location.pathname.startsWith(item.href));
                const Icon = item.icon;
                const badge = getBadge(item.badgeKey);

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => mobile && setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition",
                      active
                        ? "bg-[#eff2f5] font-semibold text-[#1f2328]"
                        : "font-medium text-[#424a53] hover:bg-[#f6f8fa]",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-[#656d76]" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {badge > 0 ? (
                      <span className="min-w-[1.25rem] rounded-full bg-[#00573C] px-1.5 py-0.5 text-center text-[11px] font-bold text-white">
                        {badge}
                      </span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-[#d0d7de] p-3">
        <div className="mb-2 px-1">
          <p className="truncate text-sm font-semibold text-[#1f2328]">{orgName}</p>
          <p className="truncate text-xs text-[#656d76]">{orgEmail}</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-sm font-medium text-[#424a53] transition hover:bg-[#f6f8fa]"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f8fa] lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] flex-col border-r border-[#d0d7de] bg-white lg:flex">
        <NavContent />
      </aside>

      {mobileOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20 lg:hidden"
            aria-label="Close overlay"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-[260px] border-r border-[#d0d7de] bg-white shadow-lg lg:hidden">
            <NavContent mobile />
          </aside>
        </>
      ) : null}

      <div className="min-w-0 lg:col-start-2">
        <header className="sticky top-0 z-20 border-b border-[#d0d7de] bg-white px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-md p-1.5 text-[#1f2328] hover:bg-[#f6f8fa] lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1 border-b-0">
              <h1 className="truncate text-xl font-semibold tracking-tight text-[#1f2328] sm:text-[1.35rem]">
                {title}
              </h1>
              {subtitle ? (
                <p className="mt-0.5 hidden truncate text-sm text-[#656d76] sm:block">
                  {subtitle}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <HeaderTimeLocation profile={profile} />
              {action ? <div className="shrink-0">{action}</div> : null}
            </div>
          </div>
        </header>

        <main className="px-4 py-6 sm:px-8 lg:px-10 lg:py-8">
          <div className="mx-auto max-w-[1120px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
